import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { get, size, isUndefined } from 'lodash'

import { Box, Button, Container, CircularProgress, Link as MuiLink, Typography } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'

import { PageTitle } from '@scoir/headers'

import api from 'common/api'

import ServerLocationStep from './ServerLocationStep'
import CredentialsStep from './CredentialsStep'
import TestConnectionStep from './TestConnectionStep'
import ActivateStep from './ActivateStep'

const hasAllFields = (state, ...fields) => {
  for (const field of fields) {
    if (isUndefined(state.Credentials[field])) {
      return false
    }
  }
  return true
}

const ActionButton = withStyles({
  outlined: {
    background: '#fff',
  },
})(Button)

export class SftpIntegration extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      Credentials: {},
      modified: {
        step0: false,
        step1: false,
      },
      testBatchSent: false,
      tested: false,
      wasActive: false,
    }
  }

  componentDidMount() {
    document.title = 'Scoir - Configure SFTP Integration'
    api.get(`/api/colleges/${this.props.scid}/integrations`).then((integrations) => {
      const sftp = get(integrations, 'SFTP', {})
      this.setState({
        ...sftp,
        wasActive: sftp.Status === 'Live',
        loaded: true,
      })
    })
  }

  updateIntegrations(idx) {
    return (integrations) => {
      const newState = {
        ...this.state,
        ...integrations.SFTP,
        tested: false,
        testBatchSent: false,
        wasActive: false,
      }
      newState.modified[`step${idx}`] = false
      this.setState(newState)
    }
  }

  saveLocation(host, port, path) {
    api
      .post(`/api/colleges/${this.props.scid}/integrations/sftp/location`, {
        Host: host,
        Port: parseInt(port, 10),
        Path: path,
      })
      .then(this.updateIntegrations(0))
  }

  savePassword(username, password) {
    return api
      .post(`/api/colleges/${this.props.scid}/integrations/sftp/credentials`, {
        Username: username,
        Password: password,
      })
      .then(this.updateIntegrations(1))
  }

  handleKeyChange(username) {
    this.savePassword(username, undefined)
  }

  handleClearCredentials() {
    api.delete(`/api/colleges/${this.props.scid}/integrations/sftp/keypair`).then(() => this.savePassword())
  }

  testConnection() {
    return api
      .get(`/api/colleges/${this.props.scid}/integrations/sftp/test`)
      .then(() => {
        this.setState({
          tested: true,
        })
      })
      .catch(() => {
        return Promise.reject()
      })
  }
  sendTestBatch() {
    return api
      .post(`/api/colleges/${this.props.scid}/integrations/sftp/test`)
      .then(() => {
        this.setState({
          testBatchSent: true,
        })
      })
      .catch(() => {})
  }

  handleModificationChange(stepIdx, isModified) {
    this.setState({
      modified: {
        ...this.state.modified,
        [`step${stepIdx}`]: isModified,
      },
    })
  }

  toggleActivation(newActivatedState) {
    const activationRoute = `/api/colleges/${this.props.scid}/integrations/sftp/activate`
    let promise
    if (newActivatedState) {
      promise = api.post(activationRoute)
    } else {
      promise = api.delete(activationRoute)
    }
    promise
      .then((integrations) => {
        this.setState({
          ...this.state,
          ...integrations.SFTP,
          wasActive: true,
        })
      })
      .catch((integrations) => {
        this.setState(integrations.SFTP)
      })
  }

  render() {
    const { Credentials, loaded, modified, Status, testBatchSent, tested, wasActive } = this.state
    return (
      <Container maxWidth="md">
        <Box my={4}>
          <PageTitle
            titleProps={{ component: 'h1' }}
            title="SFTP Integration"
            actions={
              <ActionButton
                component={MuiLink}
                href="https://scoir.helpdocs.io/article/4fbbxi0lu3-configuring-document-delivery-via-sftp"
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                variant="outlined"
              >
                Need Help?
              </ActionButton>
            }
          />
          <Typography gutterBottom>
            Scoir can also upload your student documents to your SFTP Server for you.
          </Typography>
          <Typography gutterBottom>Complete the setup below to get started!</Typography>
          {!loaded && (
            <Box
              p={8}
              textAlign="center"
            >
              <CircularProgress
                size={50}
                role="status"
                aria-label="Loading"
              />
            </Box>
          )}
          {loaded && (
            <>
              <ServerLocationStep
                hostname={Credentials.Host}
                port={Credentials.Port}
                path={Credentials.Path}
                onSubmit={(host, port, path) => this.saveLocation(host, port, path)}
                onModificationChange={(mod) => this.handleModificationChange(0, mod)}
              />
              <CredentialsStep
                scid={this.props.scid}
                editable={hasAllFields(this.state, 'Host', 'Port', 'Path') && !modified.step0}
                username={Credentials.Username}
                password={Credentials.Password}
                publicKey={Credentials.PublicKey}
                hasStoredPassword={Credentials.HasStoredPassword}
                onClearCredentials={() => this.handleClearCredentials()}
                onSavePassword={(username, password) => this.savePassword(username, password)}
                onKeyChange={(username) => this.handleKeyChange(username)}
                onModificationChange={(mod) => this.handleModificationChange(1, mod)}
              />
              <TestConnectionStep
                editable={
                  hasAllFields(this.state, 'Host', 'Port', 'Path', 'Username', 'PublicKey') &&
                  (size(Credentials.PublicKey) > 0 || Credentials.HasStoredPassword === true) &&
                  !(modified.step0 || modified.step1)
                }
                needsRetest={!tested}
                testBatchSent={testBatchSent}
                onTestConnection={() => this.testConnection()}
                onSendTestBatch={() => this.sendTestBatch()}
              />
              <ActivateStep
                editable={!(modified.step0 || modified.step1) && (Status === 'Live' || tested || wasActive)}
                activeIntegration={Status === 'Live'}
                onToggleActivation={(state) => this.toggleActivation(state)}
              />
            </>
          )}
        </Box>
      </Container>
    )
  }
}

SftpIntegration.propTypes = {
  scid: PropTypes.number.isRequired,
}

export default connect((store) => ({
  scid: store.user.record.Scid,
}))(SftpIntegration)
